import { useState } from "react";
import { ChevronDown, Info } from "lucide-react";

// Product-page information sections. Everything is written for Morocco:
// we deliver nationwide, same day, not internationally.
const COPY = {
  en: {
    features: "Extraordinary features",
    why: "Why choose this gift",
    care: "Care instructions",
    delivery: "Delivery and satisfaction",
    notesTitle: "Important notes",
    notes: "Flowers sometimes arrive still in bud so they last longer, and open within 48 hours. Every flower is unique: shades can vary slightly, and our florist may swap a stem for another of equal value to keep the bouquet flawless.",
    whyItems: [
      ["Across Morocco", "{cities} — and the same day when you order before the cut-off."],
      ["Care that shows", "Moroccan florists chosen one by one, with their own care advice in the box."],
      ["On the hour", "You pick the four-hour window, and we can call the recipient first if you'd rather not surprise them."],
      ["Premium sourcing", "Flowers bought at the market that morning and tied by hand, never pulled from a cold room."],
    ],
    careItems: [
      ["On arrival", "Put the bouquet in a clean vase filled with fresh water at room temperature as soon as it reaches you."],
      ["Feeding", "Dissolve the sachet of flower food in the vase water as directed. It carries the nutrients that keep the blooms bright and lasting."],
      ["Upkeep", "Change the water every two days and re-cut the stems at an angle under running water. That simple routine adds days to their life."],
      ["Pro tip", "Pull out any spent flower and yellowing leaf so the rest can open fully. Keep the vase out of direct sun and away from air conditioning — in Morocco that alone buys you several days."],
    ],
    deliveryItems: [
      ["Nationwide reach", "Delivered anywhere we serve in Morocco. The delivery fee depends on the neighbourhood and is shown in full before you pay — never a surprise at the door."],
      ["Proven satisfaction", "You get a photograph of the finished bouquet at the recipient's door. If anything is wrong, we replace it or refund you."],
    ],
    composition: "In this bouquet",
  },
  fr: {
    features: "Caractéristiques extraordinaires",
    why: "Pourquoi choisir ce cadeau",
    care: "Instructions d'entretien",
    delivery: "Livraison et satisfaction",
    notesTitle: "Notes importantes",
    notes: "Les fleurs arrivent parfois encore en bouton pour durer plus longtemps et s'ouvrent en 48 h. Chaque fleur est unique : les teintes peuvent varier légèrement, et notre fleuriste peut remplacer une tige par une autre de valeur égale pour garder le bouquet impeccable.",
    whyItems: [
      ["Partout au Maroc", "{cities} — et le jour même si vous commandez avant l'heure limite."],
      ["Excellence en soins", "Des fleuristes marocains sélectionnés un par un, avec leurs conseils d'entretien dans le paquet."],
      ["Livraison ponctuelle", "Vous choisissez le créneau de quatre heures, et nous pouvons appeler le destinataire avant si vous préférez éviter la surprise."],
      ["Approvisionnement premium", "Des fleurs achetées le matin même au marché et composées à la main, jamais sorties d'une chambre froide."],
    ],
    careItems: [
      ["À l'arrivée", "Placez votre bouquet dans un vase propre rempli d'eau fraîche à température ambiante dès sa réception."],
      ["Nutrition", "Dissolvez le sachet de nourriture pour fleurs dans l'eau du vase selon les instructions. Il apporte les nutriments qui gardent vos fleurs éclatantes et durables."],
      ["Entretien", "Changez l'eau tous les 2 jours et recoupez les tiges en biais sous l'eau courante. Cette simple routine prolongera la vie de vos fleurs."],
      ["Astuce de pro", "Retirez les fleurs fanées et les feuilles jaunies pour que les autres s'épanouissent. Gardez le vase loin du soleil direct et de la climatisation : au Maroc, cela suffit à gagner plusieurs jours."],
    ],
    deliveryItems: [
      ["Portée nationale", "Livré partout où nous sommes présents au Maroc. Les frais de livraison dépendent du quartier et sont affichés avant le paiement — jamais de surprise à la porte."],
      ["Satisfaction prouvée", "Vous recevez une photo du bouquet terminé devant la porte du destinataire. Si quelque chose ne va pas, nous le remplaçons ou nous vous remboursons."],
    ],
    composition: "Dans ce bouquet",
  },
  es: {
    features: "Características extraordinarias",
    why: "Por qué elegir este regalo",
    care: "Instrucciones de cuidado",
    delivery: "Entrega y satisfacción",
    notesTitle: "Notas importantes",
    notes: "A veces las flores llegan aún en capullo para durar más y se abren en 48 h. Cada flor es única: los tonos pueden variar ligeramente y nuestro florista puede sustituir un tallo por otro de igual valor para que el ramo quede impecable.",
    whyItems: [
      ["En todo Marruecos", "{cities} — y el mismo día si pide antes de la hora límite."],
      ["Excelencia en el cuidado", "Floristas marroquíes elegidos uno a uno, con sus consejos de cuidado en el paquete."],
      ["Entrega puntual", "Usted elige la franja de cuatro horas y podemos llamar antes a la persona si prefiere no sorprenderla."],
      ["Origen premium", "Flores compradas esa misma mañana en el mercado y atadas a mano, nunca sacadas de una cámara frigorífica."],
    ],
    careItems: [
      ["Al llegar", "Coloque el ramo en un jarrón limpio con agua fresca a temperatura ambiente nada más recibirlo."],
      ["Nutrición", "Disuelva el sobre de alimento para flores en el agua del jarrón según las instrucciones. Aporta los nutrientes que mantienen las flores vivas y brillantes."],
      ["Mantenimiento", "Cambie el agua cada dos días y recorte los tallos en diagonal bajo el grifo. Esa rutina sencilla alarga su vida."],
      ["Consejo profesional", "Retire las flores marchitas y las hojas amarillas para que el resto se abra. Mantenga el jarrón lejos del sol directo y del aire acondicionado: en Marruecos eso solo ya gana varios días."],
    ],
    deliveryItems: [
      ["Alcance nacional", "Entregamos en todas las ciudades donde operamos en Marruecos. El coste de entrega depende del barrio y se muestra antes de pagar, nunca una sorpresa en la puerta."],
      ["Satisfacción demostrada", "Recibirá una fotografía del ramo terminado en la puerta del destinatario. Si algo falla, lo reponemos o le devolvemos el importe."],
    ],
    composition: "En este ramo",
  },
  ar: {
    features: "مميزات استثنائية",
    why: "لماذا تختار هذه الهدية",
    care: "إرشادات العناية",
    delivery: "التوصيل والرضا",
    notesTitle: "ملاحظات مهمة",
    notes: "قد تصل الزهور وهي لا تزال براعم لتدوم أطول، وتتفتح خلال 48 ساعة. كل زهرة فريدة: قد تختلف الألوان قليلاً، وقد يستبدل بائع الزهور ساقاً بأخرى من القيمة نفسها ليبقى الباقة في أبهى صورة.",
    whyItems: [
      ["في كل أنحاء المغرب", "{cities} — وفي اليوم نفسه إذا طلبت قبل الموعد النهائي."],
      ["عناية تُرى", "بائعو زهور مغاربة مختارون واحداً واحداً، مع نصائح العناية داخل الطلب."],
      ["توصيل في موعده", "أنت تختار نافذة الأربع ساعات، ويمكننا الاتصال بالمستلم مسبقاً إن كنت تفضل عدم المفاجأة."],
      ["مصدر متميز", "زهور تُشترى من السوق في الصباح نفسه وتُنسَّق يدوياً، لا تُخرج من غرف تبريد."],
    ],
    careItems: [
      ["عند الوصول", "ضع الباقة في مزهرية نظيفة مملوءة بماء عذب بدرجة حرارة الغرفة فور استلامها."],
      ["التغذية", "أذب كيس غذاء الزهور في ماء المزهرية حسب التعليمات. فهو يمنح العناصر التي تبقي الزهور نضرة وأطول عمراً."],
      ["المتابعة", "غيّر الماء كل يومين وأعد قص السيقان بزاوية تحت الماء الجاري. هذه العادة البسيطة تطيل عمر الزهور."],
      ["نصيحة محترف", "انزع الزهور الذابلة والأوراق المصفرّة ليتفتح الباقي. أبعد المزهرية عن الشمس المباشرة والمكيّف: في المغرب هذا وحده يكسبك أياماً إضافية."],
    ],
    deliveryItems: [
      ["تغطية وطنية", "نوصل في كل المدن التي نخدمها في المغرب. تعتمد رسوم التوصيل على الحي وتظهر كاملة قبل الدفع، بلا أي مفاجأة عند الباب."],
      ["رضا مثبت", "تصلك صورة للباقة أمام باب المستلم. وإذا حدث أي خلل، نستبدلها أو نعيد لك المبلغ."],
    ],
    composition: "في هذه الباقة",
  },
  it: {
    features: "Caratteristiche straordinarie",
    why: "Perché scegliere questo regalo",
    care: "Istruzioni per la cura",
    delivery: "Consegna e soddisfazione",
    notesTitle: "Note importanti",
    notes: "I fiori a volte arrivano ancora in boccio per durare più a lungo e si aprono entro 48 ore. Ogni fiore è unico: le sfumature possono variare leggermente e il nostro fiorista potrebbe sostituire uno stelo con un altro di pari valore per mantenere il bouquet impeccabile.",
    whyItems: [
      ["In tutto il Marocco", "{cities} — e in giornata se ordina prima dell'orario limite."],
      ["Cura che si vede", "Fioristi marocchini selezionati uno per uno, con i propri consigli di cura inclusi nella scatola."],
      ["Fascia oraria a scelta", "Sceglie la finestra di quattro ore, e possiamo contattare il destinatario in anticipo se preferisce non sorprenderlo."],
      ["Approvvigionamento premium", "Fiori acquistati al mercato la mattina stessa e legati a mano, mai prelevati da una cella frigorifera."],
    ],
    careItems: [
      ["All'arrivo", "Metta il bouquet in un vaso pulito riempito con acqua fresca a temperatura ambiente non appena lo riceve."],
      ["Nutrimento", "Sciolga la bustina di nutrimento per fiori nell'acqua del vaso seguendo le istruzioni. Apporta i nutrienti che mantengono i fiori vivaci e duraturi."],
      ["Manutenzione", "Cambi l'acqua ogni due giorni e ritaglia gli steli in diagonale sotto l'acqua corrente. Questa semplice routine prolunga la vita dei fiori di diversi giorni."],
      ["Consiglio dell'esperto", "Rimuova i fiori appassiti e le foglie ingiallite per permettere agli altri di aprirsi pienamente. Tenga il vaso lontano dalla luce diretta del sole e dall'aria condizionata — in Marocco, questo accorgimento da solo guadagna diversi giorni."],
    ],
    deliveryItems: [
      ["Copertura nazionale", "Consegna ovunque serviamo in Marocco. Il costo di consegna dipende dal quartiere ed è mostrato per intero prima del pagamento — nessuna sorpresa alla porta."],
      ["Soddisfazione garantita", "Riceverà una fotografia del bouquet completato alla porta del destinatario. Se qualcosa non va, lo sostituiamo o la rimborsiamo."],
    ],
    composition: "In questo bouquet",
  },
  nl: {
    features: "Bijzondere kenmerken",
    why: "Waarom dit cadeau kiezen",
    care: "Verzorgingsinstructies",
    delivery: "Bezorging en tevredenheid",
    notesTitle: "Belangrijke opmerkingen",
    notes: "Bloemen komen soms nog in knop aan zodat ze langer meegaan en opengaan binnen 48 uur. Elke bloem is uniek: tinten kunnen licht variëren en onze bloemist kan een steel vervangen door een andere van gelijke waarde om het boeket perfect te houden.",
    whyItems: [
      ["Door heel Marokko", "{cities} — en dezelfde dag als u vóór de sluitingstijd bestelt."],
      ["Zorg die zichtbaar is", "Marokkaanse bloemisten één voor één geselecteerd, met hun eigen verzorgingsadvies in de doos."],
      ["Op het uur nauwkeurig", "U kiest het tijdvenster van vier uur en we kunnen de ontvanger vooraf bellen als u hem of haar liever niet wilt verrassen."],
      ["Premium inkoop", "Bloemen die 's ochtends op de markt zijn gekocht en met de hand gebonden, nooit uit een koelcel gehaald."],
    ],
    careItems: [
      ["Bij aankomst", "Zet het boeket zodra het bij u is in een schone vaas met vers water op kamertemperatuur."],
      ["Voeding", "Los het zakje bloemenvoeding op in het vaaswater zoals aangegeven. Het bevat de voedingsstoffen die de bloemen helder en langdurig houden."],
      ["Onderhoud", "Ververs het water om de twee dagen en knip de stelen schuin af onder stromend water. Deze simpele routine verlengt hun leven met meerdere dagen."],
      ["Pro-tip", "Verwijder verwelkte bloemen en vergeelde bladeren zodat de rest volledig kan opengaan. Zet de vaas uit de directe zon en weg van de airconditioning — in Marokko levert dat alleen al meerdere dagen extra op."],
    ],
    deliveryItems: [
      ["Landelijke dekking", "Bezorgd overal waar we actief zijn in Marokko. De bezorgkosten hangen af van de wijk en worden volledig getoond vóór betaling — nooit een verrassing aan de deur."],
      ["Bewezen tevredenheid", "U ontvangt een foto van het voltooide boeket aan de deur van de ontvanger. Als er iets niet klopt, vervangen we het of betalen we u terug."],
    ],
    composition: "In dit boeket",
  },
  de: {
    features: "Außergewöhnliche Eigenschaften",
    why: "Warum dieses Geschenk wählen",
    care: "Pflegehinweise",
    delivery: "Lieferung und Zufriedenheit",
    notesTitle: "Wichtige Hinweise",
    notes: "Blumen kommen manchmal noch als Knospe an, damit sie länger halten, und öffnen sich innerhalb von 48 Stunden. Jede Blume ist einzigartig: Farbtöne können leicht variieren, und unser Florist kann einen Stiel durch einen anderen gleichwertigen ersetzen, um den Strauß makellos zu halten.",
    whyItems: [
      ["Durch ganz Marokko", "{cities} — und noch am selben Tag, wenn Sie vor dem Bestellschluss bestellen."],
      ["Fürsorge, die man sieht", "Marokkanische Floristen, einzeln ausgewählt, mit ihren eigenen Pflegehinweisen in der Box."],
      ["Pünktlich auf die Stunde", "Sie wählen das Vier-Stunden-Fenster, und wir können den Empfänger vorab anrufen, wenn Sie ihn lieber nicht überraschen möchten."],
      ["Premium-Einkauf", "Blumen, die morgens auf dem Markt gekauft und von Hand gebunden werden – nie aus einem Kühlraum geholt."],
    ],
    careItems: [
      ["Bei Ankunft", "Stellen Sie den Strauß sofort in eine saubere Vase mit frischem Wasser bei Zimmertemperatur, sobald er bei Ihnen ankommt."],
      ["Ernährung", "Lösen Sie das Päckchen Blumennahrung wie angegeben im Vasenwasser auf. Es liefert die Nährstoffe, die die Blüten frisch und langlebig halten."],
      ["Pflege", "Wechseln Sie das Wasser alle zwei Tage und schneiden Sie die Stiele schräg unter fließendem Wasser ab. Diese einfache Routine verlängert die Lebensdauer um mehrere Tage."],
      ["Profi-Tipp", "Entfernen Sie verwelkte Blüten und gilbende Blätter, damit sich die übrigen voll entfalten können. Stellen Sie die Vase aus der direkten Sonne und weg von der Klimaanlage – in Marokko gewinnen Sie damit allein schon mehrere Tage."],
    ],
    deliveryItems: [
      ["Landesweite Abdeckung", "Lieferung überall dort, wo wir in Marokko tätig sind. Die Liefergebühr hängt vom Stadtteil ab und wird vor der Zahlung vollständig angezeigt – keine Überraschung an der Tür."],
      ["Bewährte Zufriedenheit", "Sie erhalten ein Foto des fertigen Straußes an der Tür des Empfängers. Wenn etwas nicht stimmt, ersetzen wir ihn oder erstatten Ihnen den Betrag."],
    ],
    composition: "In diesem Strauß",
  },
};

const Accordion = ({ title, open, onToggle, testid, children }) => (
  <div className="border-b border-brass-light">
    <button type="button" data-testid={testid} aria-expanded={open} onClick={onToggle}
            className="w-full flex items-center gap-3 py-4 text-start group">
      <span className="font-display text-lg md:text-xl text-ink flex-1">{title}</span>
      <ChevronDown size={18}
                   className={`text-brass shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
    </button>
    <div className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
      <div className="overflow-hidden">
        <div className="pb-5 space-y-3 text-sm leading-relaxed text-charcoal">{children}</div>
      </div>
    </div>
  </div>
);

const Lead = ({ label, body, sep }) => (
  <p><span className="text-ink">{label}{sep}</span> {body}</p>
);

export const ProductInfoSections = ({ locale = "en", product, cities = [] }) => {
  const c = COPY[locale] || COPY.en;
  const [open, setOpen] = useState(() => new Set(["features", "why"]));
  const toggle = (key) => setOpen((v) => {
    const next = new Set(v);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const sep = locale === "fr" ? " :" : ":";  // French keeps the space before the colon
  const cityNames = cities.map((x) => x.name).join(", ");

  return (
    <div data-testid="product-info-sections">
      <Accordion title={c.features} open={open.has("features")} onToggle={() => toggle("features")}
                 testid="product-acc-features">
        <p>{product.description}</p>
        {product.flower_composition?.length ? (
          <p className="text-xs text-muted">
            <span className="smallcaps text-brass">{c.composition}</span>{" "}
            {product.flower_composition.join(" · ")}
          </p>
        ) : null}
      </Accordion>

      <Accordion title={c.why} open={open.has("why")} onToggle={() => toggle("why")} testid="product-acc-why">
        {c.whyItems.map(([label, body]) => (
          <Lead key={label} label={label} sep={sep} body={body.replace("{cities}", cityNames)} />
        ))}
      </Accordion>

      <Accordion title={c.care} open={open.has("care")} onToggle={() => toggle("care")} testid="product-acc-care">
        {c.careItems.map(([label, body]) => <Lead key={label} label={label} sep={sep} body={body} />)}
        {product.care_instructions ? <p className="text-xs text-muted">{product.care_instructions}</p> : null}
      </Accordion>

      <Accordion title={c.delivery} open={open.has("delivery")} onToggle={() => toggle("delivery")}
                 testid="product-acc-delivery">
        {c.deliveryItems.map(([label, body]) => <Lead key={label} label={label} sep={sep} body={body} />)}
      </Accordion>

      <div className="mt-6 flex gap-3 bg-plaster-deep/60 border border-brass-light p-4" data-testid="product-important-notes">
        <Info size={16} className="text-brass shrink-0 mt-0.5" />
        <p className="text-xs leading-relaxed text-charcoal">
          <span className="text-ink">{c.notesTitle}{sep}</span> {c.notes}
        </p>
      </div>
    </div>
  );
};

export default ProductInfoSections;
